import { useCallback, useEffect, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { getApiUrl } from '../config/appConfig';
import { getAccessToken } from '../utils/sessionAuthStore';
import type {
  CrmOportunidadRealtimeEvent,
  CrmRealtimeConnectionState,
  CrmRealtimeEventType,
} from '../types/crmRealtime';

const EVENT_NAMES: CrmRealtimeEventType[] = [
  'OportunidadCreada',
  'OportunidadEtapaCambiada',
  'OportunidadCerradaGanada',
  'OportunidadCerradaPerdida',
];

export type CrmRealtimeHandler = (evt: CrmOportunidadRealtimeEvent) => void;

function hubBaseUrl(): string {
  const api = getApiUrl().replace(/\/$/, '');
  if (api) return api;
  // Dev con apiUrl vacío (proxy CRA): ir directo a la API para no usar
  // el WebSocket del proxy, que pelea con el HMR en /ws.
  if (process.env.NODE_ENV === 'development') {
    return (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');
  }
  return typeof window !== 'undefined' ? window.location.origin : '';
}

/**
 * Conecta al hub CRM mientras el componente esté montado.
 * Usar solo en rutas /crm/* (p. ej. pipeline / detalle).
 */
export function useCrmRealtime(onEvent: CrmRealtimeHandler, enabled = true) {
  const [connectionState, setConnectionState] = useState<CrmRealtimeConnectionState>('disconnected');
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  const stableHandler = useCallback((evt: CrmOportunidadRealtimeEvent) => {
    handlerRef.current(evt);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setConnectionState('disconnected');
      return;
    }

    let cancelled = false;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${hubBaseUrl()}/hubs/crm`, {
        accessTokenFactory: () => getAccessToken() || '',
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    EVENT_NAMES.forEach((name) => {
      connection.on(name, (payload: CrmOportunidadRealtimeEvent) => {
        const evt = { ...payload, tipo: payload.tipo || name };
        stableHandler(evt);
      });
    });

    connection.onreconnecting(() => {
      if (!cancelled) setConnectionState('reconnecting');
    });
    connection.onreconnected(() => {
      if (!cancelled) setConnectionState('connected');
    });
    connection.onclose(() => {
      if (!cancelled) setConnectionState('disconnected');
    });

    setConnectionState('connecting');
    connection
      .start()
      .then(() => {
        if (!cancelled) setConnectionState('connected');
      })
      .catch(() => {
        if (!cancelled) setConnectionState('disconnected');
      });

    return () => {
      cancelled = true;
      EVENT_NAMES.forEach((name) => connection.off(name));
      void connection.stop();
    };
  }, [enabled, stableHandler]);

  return { connectionState };
}
